import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError } from 'rxjs/operators';
import { EMPTY, throwError } from 'rxjs';
import { AuthService } from '../../shared/services/auth.services';
import { SimulationService } from '../../shared/services/simulation.service';
import { GlobalMessageService } from '../../shared/services/global-message.service';
import { isPreprodFullAccess } from '../config/preprod';

const WRITE_METHODS = ['POST', 'PUT', 'DELETE', 'PATCH'];

/**
 * Consultas/validações modeladas como POST que NÃO alteram estado e, por isso, são
 * liberadas mesmo no modo simulação somente-leitura (amarelinho).
 *
 * IMPORTANTE: manter em espelho com a allowlist do backend em
 * pgpp-back/.../filter/SimulationReadOnlyInterceptor.java (READ_ONLY_POST_ALLOWLIST).
 */
const SIMULATION_READONLY_POST_ALLOWLIST: RegExp[] = [
  /\/api\/profissional(-homol)?\/validar-homologacao$/,
  // Relatório Gerencial: consulta/contagem/exportação são POST somente-leitura
  // (a definição do relatório vai no corpo).
  /\/api\/relatorio-gerencial\/[^/]+\/[^/]+\/(consulta|contagem|exportar|exportar-assincrono)$/,
];

export const HttpsRequestInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const simulationService = inject(SimulationService);
  const globalMessageService = inject(GlobalMessageService);
  const token = authService.getAccessToken();

  // Curto-circuito de escrita no modo simulação somente-leitura (amarelinho).
  // Espelha o SimulationReadOnlyInterceptor do backend: o critério de "escrita" é o
  // verbo HTTP — não a nomenclatura da role. Bloqueia antes de ir ao servidor (feedback
  // imediato), exceto endpoints de leitura modelados como POST (allowlist) e os próprios
  // endpoints de simulação. Em pre-prod (full access) e para o super-simulador
  // (writeEnabled) nada é bloqueado, espelhando as exceções do backend.
  if (
    simulationService.isSimulating() &&
    !simulationService.isWriteEnabled() &&
    !isPreprodFullAccess() &&
    WRITE_METHODS.includes(req.method)
  ) {
    const urlPath = req.url.split('?')[0];
    const isSimulationEndpoint = urlPath.includes('/api/simulacao/');
    const isAllowlisted = SIMULATION_READONLY_POST_ALLOWLIST.some((pattern) => pattern.test(urlPath));

    if (!isSimulationEndpoint && !isAllowlisted) {
      globalMessageService.addMessage({
        type: 'warning',
        message: 'Ação indisponível no modo simulação (somente leitura).',
      });
      // Encerra o stream sem erro (em vez de propagar um 403): a ação fica bloqueada
      // client-side com mensagem única. Propagar o erro faria o handler de erro do
      // componente exibir uma segunda mensagem (modal) para o mesmo clique. O finalize
      // do componente ainda roda (EMPTY completa), desligando spinners/loading.
      return EMPTY;
    }
  }

  const authReq = token
    ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
    : req;

  return next(authReq).pipe(
    catchError((err: unknown) => {
      // Sem token = acesso anônimo (ex.: HU059 pública). Não tratar como sessão expirada.
      if (err instanceof HttpErrorResponse && err.status === 401 && token) {
        authService.sessionTimeout();
      }
      return throwError(() => err);
    })
  );
};
